import keyBy from 'lodash/keyBy';
import reduce from 'lodash/reduce';

import { TFunction } from '@lyearn/i18n';
import { CornerDownRightOutline, EditOutline, PlusFill, Trash2Outline } from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';

import { CategoryFileFragment } from '../graphql/fragment/generated/CategoryFile';
import { CategoryActionsEnum, CategoryType } from '../types';

export const getCategoryActionMenuItems = (t: TFunction): ActionMenuItemType[] => [
  {
    id: CategoryActionsEnum.RenameCategory,
    label: t('Common:Rename'),
    EndIcon: EditOutline,
  },
  {
    id: CategoryActionsEnum.AddSubcategory,
    label: t('Discover:Category.Admin.AddSubcategory'),
    EndIcon: PlusFill,
  },
  {
    id: CategoryActionsEnum.MergeCategory,
    label: t('Discover:Category.Admin.MergeCategory'),
    EndIcon: CornerDownRightOutline,
  },
  {
    id: CategoryActionsEnum.DeleteCategory,
    label: t('Common:Actions.Delete'),
    EndIcon: Trash2Outline,
    appearance: 'danger',
  },
];

export const searchCategoryFromTree = (categoryTrees: CategoryType[], searchCategoryId: string) => {
  if (!categoryTrees.length) {
    return null;
  }

  return reduce(
    categoryTrees,
    (found: CategoryType | null, category): CategoryType | null => {
      if (found) {
        return found;
      }

      if (category.id === searchCategoryId) {
        return category;
      } else if (category.childCategories?.length) {
        return searchCategoryFromTree(category.childCategories, searchCategoryId);
      }

      return null;
    },
    null,
  );
};

const filterCategories = (
  categoriesTree: CategoryType[],
  predicate: (category: CategoryType) => boolean,
): CategoryType[] => {
  if (!categoriesTree.length) {
    return categoriesTree;
  }
  return reduce(
    categoriesTree,
    (acc, category) => {
      if (predicate(category)) {
        acc.push(category);
      } else if (category.childCategories) {
        const childrenMatches = filterCategories(category.childCategories, predicate);
        acc.push(...childrenMatches);
      }
      return acc;
    },
    [] as CategoryType[],
  );
};

export const filterCategoriesByIds = (categoriesTree: CategoryType[] | null, ids: string[]) => {
  if (!categoriesTree?.length || !ids?.length) {
    return [];
  }
  return filterCategories(categoriesTree, (category) => ids.includes(category.id));
};

export const filterCategoriesByText = (
  categoriesTree: CategoryType[] | null,
  searchText: string,
) => {
  if (!categoriesTree?.length) {
    return [];
  }

  if (!searchText.trim()) {
    return categoriesTree;
  }

  return filterCategories(categoriesTree, (category) =>
    category.name.toLowerCase().includes(searchText.toLowerCase()),
  );
};

export const filterCategoriesByExcludedIds = (
  categoriesTree: CategoryType[] | null,
  excludedIds: string[],
) => {
  if (!categoriesTree?.length) {
    return [];
  }

  if (!excludedIds.length) {
    return categoriesTree;
  }

  return reduce(
    categoriesTree,
    (acc, category) => {
      if (excludedIds.includes(category.id)) {
        return acc;
      }

      if (category.childCategories) {
        const childrenMatches = filterCategoriesByExcludedIds(
          category.childCategories,
          excludedIds,
        );
        category.childCategories = childrenMatches;
      }

      acc.push(category);
      return acc;
    },
    [] as CategoryType[],
  );
};

export const getInitialExpandedState = ({
  isSelected,
  categoriesTree,
}: {
  isSelected: (key: string) => boolean;
  categoriesTree?: CategoryType[] | null;
}): boolean => {
  if (!categoriesTree?.length) {
    return false;
  }
  return categoriesTree.some((category) => {
    return (
      isSelected(category.id) ||
      getInitialExpandedState({ isSelected, categoriesTree: category.childCategories })
    );
  });
};

export const buildCategoriesMap = (categories: CategoryFileFragment[]) => keyBy(categories, 'id');

export const filterSubworkspaceCategories = ({
  categoryIds,
  subWorkspaceIds,
  categories,
}: {
  categoryIds: string[];
  subWorkspaceIds: string[];
  categories?: CategoryFileFragment[];
}): string[] => {
  if (!categories?.length) {
    return [];
  }

  const categoriesMap = buildCategoriesMap(categories);

  return categoryIds.filter(
    (id) =>
      categoriesMap[id]?.subWorkspaces?.some((subWorkspace) =>
        subWorkspaceIds.some((key) => subWorkspace.includes(key)),
      ),
  );
};
